import urlConstants from '~/lib/constants/url.constants';

import api from './api';
import { CacheTag } from './CacheTag';
import { HttpHeaders, ContentTypes, HttpRequestMethod } from '..';
import { ENUMERATION_CACHE_TIME } from '~/store/services/enumerations';

export interface GetProvidersResponseV2 {
  count: number;
  data: GetProviderByIdResponse[];
}

interface GetProvidersV2Request {
  'include-calendar-profile'?: boolean;
  'provider-search'?: string;
  status?: 'active' | 'inactive';
  'page-number'?: number;
  'page-size'?: number;
  'include-avatar'?: boolean;
  id?: string[];
  'include-provider-history'?: boolean;
  'include-out-of-office'?: boolean;
  'sort-by'?: string;
  role?: string[];
  'app-access'?: string[];
  timezone?: string[];
  'is-supervisor'?: boolean;
  'supervisor-id'?: string[];
  'include-email-search'?: boolean;
}

interface EditPermissionRequest {
  accountStatus?: string;
  appAccess?: string[];
  permissions: string[];
  roles: string[];
  providerExternalId: string;
}

interface UserResponse {
  id: string;
}
interface EditProviderByIdResponse {
  id: string;
}

interface EditProviderAvatarByIdResponse {
  fileName: string;
  fileScanStatus: string;
}

interface EditProviderAvatarByIdRequestBody {
  providerExternalId: string;
  editData: FormData;
}

export interface ProviderOutOfOfficeResponse {
  providerExternalId: string;
  outOfOfficeId: string;
  startDate: string;
  endDate: string;
}

interface ProviderOutOfOfficeRequest {
  providerExternalIds: string[];
  fromStartDate: string;
}
interface EditAdminProviderByIdResponse {
  id: string;
}
interface EditProviderByIdRequestBody {
  providerExternalId: string;
  editData: EditProviderFields;
}
interface EditAdminProviderByIdRequestBody {
  providerExternalId: string;
  editData: AddEditProviderRequestBody;
}

export interface GetProviderAvatarByIdResponse {
  url?: string;
  fileScanStatus?: string;
  thumbnailUrl?: string;
}

export const providersApi = api.injectEndpoints({
  endpoints: (build) => ({
    addUser: build.mutation<UserResponse, AddEditProviderRequestBody>({
      query: (addData) => ({
        body: addData,
        method: 'POST',
        url: urlConstants.users.addUser,
      }),
    }),
    deleteProviderAvatarById: build.mutation<
      void,
      { providerExternalId: string }
    >({
      invalidatesTags: (_, error, { providerExternalId }) =>
        error
          ? []
          : [
              { type: CacheTag.ProviderProfileAvatar, id: providerExternalId },
              { type: CacheTag.ProfileInfo, id: providerExternalId },
            ],
      query: ({ providerExternalId }) => ({
        headers: {
          [HttpHeaders.CONTENT_TYPE]: ContentTypes.JSON,
        },
        method: HttpRequestMethod.DELETE,
        url: urlConstants.user_profile.deleteUserAvatarById(providerExternalId),
      }),
    }),
    editAdminProviderById: build.mutation<
      EditAdminProviderByIdResponse,
      EditAdminProviderByIdRequestBody
    >({
      query: ({ providerExternalId, editData }) => ({
        body: editData,
        method: 'PUT',
        url: `${urlConstants.users.editAdminUserById(providerExternalId)}`,
      }),
    }),
    editProviderAvatarById: build.mutation<
      EditProviderAvatarByIdResponse,
      EditProviderAvatarByIdRequestBody
    >({
      invalidatesTags: (_, error, { providerExternalId }) =>
        error
          ? []
          : [
              { type: CacheTag.ProviderProfileAvatar, id: providerExternalId },
              { type: CacheTag.ProfileInfo, id: providerExternalId },
            ],
      query: ({ providerExternalId, editData }) => ({
        body: editData,
        method: 'PUT',
        url: `${urlConstants.user_profile.editUserAvatarById(providerExternalId)}`,
      }),
    }),
    editProviderById: build.mutation<
      EditProviderByIdResponse,
      EditProviderByIdRequestBody
    >({
      query: ({ providerExternalId, editData }) => ({
        body: editData,
        method: 'PUT',
        url: `${urlConstants.users.editUserById(providerExternalId)}`,
      }),
    }),
    getAllProvidersV2: build.query<
      GetProvidersResponseV2,
      GetProvidersV2Request | void
    >({
      query: (args) => {
        const defaultParams: GetProvidersV2Request = {
          'include-avatar': true,
          'page-number': 1,
          'page-size': 100,
        };
        const params: GetProvidersV2Request = {
          ...defaultParams,
          ...args,
        };
        return {
          params,
          url: urlConstants.users.listUserV2,
        };
      },
    }),

    getProviderAvatarById: build.query<
      GetProviderAvatarByIdResponse,
      { providerExternalId: string }
    >({
      providesTags: (_, __, { providerExternalId }) => [
        { type: CacheTag.ProviderProfileAvatar, id: providerExternalId },
      ],
      query: ({ providerExternalId }) => {
        return urlConstants.user_profile.getAvatarById(providerExternalId);
      },
      keepUnusedDataFor: ENUMERATION_CACHE_TIME,
    }),
    getProviderById: build.query<
      GetProviderByIdResponse,
      { provideAuthDetails?: boolean; providerExternalId: string }
    >({
      providesTags: (_, __, { providerExternalId }) => [
        { type: CacheTag.ProfileInfo, id: providerExternalId },
      ],
      query: ({ providerExternalId, provideAuthDetails = false }) => {
        return urlConstants.users.userById(
          providerExternalId,
          provideAuthDetails,
        );
      },
      keepUnusedDataFor: 5 * 60,
    }),
    getProviderInfoWithHistoryById: build.query<
      GetProviderByIdResponse,
      {
        providerExternalId: string;
        includeProviderHistory?: boolean;
        includeAvatar?: boolean;
        includeOutOfOffice?: boolean;
        provideAuthDetails?: boolean;
      }
    >({
      providesTags: (_, __, { providerExternalId }) => [
        { type: CacheTag.ProfileInfo, id: providerExternalId },
      ],
      query: ({
        providerExternalId,
        includeProviderHistory = false,
        includeAvatar = false,
        includeOutOfOffice = false,
        provideAuthDetails = false,
      }) => {
        return urlConstants.users.getProviderInfoWithHistoryById(
          providerExternalId,
          includeProviderHistory,
          includeAvatar,
          includeOutOfOffice,
          provideAuthDetails,
        );
      },
      keepUnusedDataFor: 5 * 60, // TODO more? invalidate cache logic?
    }),
    getProviderOutOfOfficeDetails: build.query<
      ProviderOutOfOfficeResponse[],
      ProviderOutOfOfficeRequest
    >({
      query: (request: ProviderOutOfOfficeRequest) => {
        return {
          params: {
            fromStartDate: request.fromStartDate,
            providerExternalIds: request.providerExternalIds,
          },
          url: urlConstants.providers.outOfOffice,
        };
      },
    }),

    inviteProvider: build.mutation<void, { providerExternalId: string }>({
      query: ({ providerExternalId }) => ({
        headers: {
          'content-type': 'application/json',
        },
        method: 'POST',
        responseHandler: 'text',
        url: urlConstants.patients.inviteProvider(providerExternalId),
      }),
    }),

    updateProviderAboutMeById: build.mutation<void, ProviderAboutMeRequest>({
      invalidatesTags: [CacheTag.ProfileInfo],
      query: ({
        providerExternalId,
        professionalExperience,
        connectWithMe,
      }) => ({
        body: {
          connectWithMe: connectWithMe.trim(),
          professionalExperience: professionalExperience.trim(),
        },
        method: 'PATCH',
        url: urlConstants.users.editAboutMeById(providerExternalId),
      }),
    }),

    updateProviderPermissions: build.mutation<void, EditPermissionRequest>({
      invalidatesTags: [CacheTag.ProfileInfo],
      query: ({
        providerExternalId,
        appAccess,
        accountStatus,
        permissions,
        roles,
      }) => ({
        body: {
          accountStatus,
          appAccess,
          permissions,
          roles,
        },
        method: 'PATCH',
        url: urlConstants.users.editPermissionsById(providerExternalId),
      }),
    }),
  }),
});

export const {
  useAddUserMutation,
  useDeleteProviderAvatarByIdMutation,
  useGetAllProvidersV2Query,
  useLazyGetAllProvidersV2Query,
  useGetProviderAvatarByIdQuery,
  useGetProviderInfoWithHistoryByIdQuery,
  useLazyGetProviderAvatarByIdQuery,
  useGetProviderByIdQuery,
  useLazyGetProviderByIdQuery,
  useEditProviderAvatarByIdMutation,
  useEditProviderByIdMutation,
  useEditAdminProviderByIdMutation,
  useGetProviderOutOfOfficeDetailsQuery,
  useInviteProviderMutation,
  useUpdateProviderPermissionsMutation,
  useUpdateProviderAboutMeByIdMutation,
} = providersApi;
